/* eslint-disable no-param-reassign, func-names */
import { ToWords } from 'to-words';

export default function install(Vue) {
  const _vue = Vue;
  if (install.installed) return;
  install.installed = true;
  _vue.prototype.$creator = _vue.$creator = 'Nicolas Chesné'; // eslint-disable-line
  _vue.mixin({
    computed: {
      /**
       * Check if the current device is mobile.
       * @returns {boolean}
       */
      $isMobile() {
        return this.$store.state.isMobile;
      },

      /**
       * Get the current language version.
       * @returns {string}
       */
      $lang() {
        return this.$store.state.version;
      },

      /**
       * Check if the current language is French.
       * @returns {boolean}
       */
      $isFr() {
        return this.$lang === 'fr';
      },

      /**
       * Check if the current language is English.
       * @returns {boolean}
       */
      $isEn() {
        return this.$lang === 'en';
      },

      /**
       * Return the current route name, following this priority :
       * If a meta name is defined, use it. It can be used to override the route name and set a duplicate name in case of nested routes
       * If the route has a name is defined, use it. Defaut behavior
       * If the route has not name, tries to defaut to the first matched route name. That cas be usefull in case of nested routes with no meta
       * @returns
       */
      $routeName() {
        return this.$route.meta.name || this.$route.name || this.$route.matched[0].name || '';
      },

      /**
       * Get the content configuration for the current route.
       * @returns {Object}
       */
      $content() {
        return this.$store.state.config[this.$routeName];
      },

      /**
       * Get the raw configuration object.
       * @returns {Object}
       */
      $config() {
        return this.$store.state.config;
      },

      /**
       * Return the MDP Config attributes
       * @returns Returns the current MDP attributes object
       */
      $attributes() {
        return this.$store.state.attributes;
      },

      /**
       * Get the collections from the store.
       * @returns {Array}
       */
      $collections() {
        return this.$store.getters.getCollections;
      },

      /**
       * Get the products from the store.
       * @returns {Array}
       */
      $products() {
        return this.$store.getters.getProducts;
      },

      /**
       * Get the current wave from the store.
       * @returns {Object}
       */
      $currentWave() {
        return this.$store.getters.getCurrentWave;
      },

      /**
       * Can be used to toggle debug mode in the app.
       * @returns {boolean} Wether we are in debug mode or not.
       */
      $debug() {
        return this.$store.state.attributes.debug || this.$store.state.debug || false;
      },
      /**
       * Get the current URL parameters.
       * @returns {URLSearchParams}
       */
      $fullUrlParams() {
        const searchParams = new URLSearchParams(window.location.search);

        // Check if there are parameters after the hash
        const hashParts = window.location.hash.split('?');
        if (hashParts.length > 1) {
          // Add parameters after the hash to our URLSearchParams object
          const hashParams = new URLSearchParams(hashParts[1]);
          hashParams.forEach((value, key) => {
            searchParams.append(key, value);
          });
        }

        return searchParams;
      },
    },

    methods: {
      $getToWordsInstance() {
        if (!this.toWordsInstance) {
          this.toWordsInstance = new ToWords({ localeCode: this.$isEn ? 'en-IN' : 'fr-FR' });
        }
        return this.toWordsInstance;
      },

      /**
       * Get a product by its ID.
       * @param {number} id - The product ID.
       * @returns {Object}
       */
      $getProductById(id) {
        return this.$store.getters.getProductById(id);
      },

      /**
       * Get products by a specific value.
       * @param {string} value - The value to filter products by.
       * @returns {Array}
       */
      $getProductsByValue(value) {
        return this.$store.getters.getProductsByValue(value);
      },

      /**
       * Get products by their collection ID.
       * @param {number} id - The collection ID.
       * @returns {Array}
       */
      $getProductsByCollectionId(id) {
        return this.$store.getters.getProductsByCollectionId(id);
      },

      /**
       * Get a collection by its ID.
       * @param {number} id - The collection ID.
       * @returns {Object}
       */
      $getCollectionById(id) {
        return this.$store.getters.getCollectionById(id);
      },

      /**
       * Filter products by wave according to their entering wave number (wave attribute) and exit wave number (exitWave attribute).
       * @param {Array} products - The list of products.
       * @param {number} wave - The wave number to filter by.
       * @returns {Array}
       */
      $filterByWave(products, wave) {
        return products.filter(product => product.wave <= wave && (product.exitWave === undefined || product.exitWave > wave));
      },

      /**
       * Exclude products by wave.
       * for instance, excuding products that are not yet available in the current wave.
       * eg: If wave is set to 2, only roducts wave 3 and above will be returned.
       * @param {Array} products - The list of products.
       * @param {number} wave - The wave number to exclude by.
       * @returns {Array}
       */
      $excludeByWave(products, wave) {
        return products.filter(product => product.wave > wave);
      },
      /**
       * Get product featured in a single wave
       * @param {Array} products - The list of products.
       * @param {number} wave - The wave number to exclude by.
       * @returns {Array}
       */
      $getUniqueWave(products, wave) {
        return products.filter(product => product.wave === wave);
      },
    },
  });
}
